/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import React, { useState, useEffect, useCallback } from "react";

import Demographic from "../../assets/demographic.svg";
import Question from "../../assets/question-icon.svg";
import Threshold from "../../assets/threshold-icon.svg";
import { extendedApiWrapper, QuestionListResponse } from "../../services/api-handlers";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { setFilters, resetSelectedFilters, setDemographicOptions, resetDemographicOptions } from "../../store/selected-filter-slice";
import { fetchSurveyDetails } from "../../store/survey-details-slice";
import { CustomCancelButton, CustomTranslateButton } from "../../styled/mui-styled";
import { DefaultFilters } from "../../utils/utility-function";

import TextInput from "../input/text-input";

import DemographicPermissions from "./demographic-permissions";
import DropdownWithCheckboxes, { Option } from "./dropdown";
import styles from "./styles.module.css";

interface SurveyDetailsProps {
    surveyId: string | null;
    preSelectedQuestions: Option[];
    handleEditCancel: () => void;
    onSubmitData: (data: { selectedFilters: { [key: string]: string[] }; selectedQuestions: string[]; threshold: number | ""; selectedDemographicOptionsFilters: { [key: string]: string[] } }) => void;
    savedDemographicPermissions: DefaultFilters;
    savedThreshold: number;
    savedDemographicOptions: {
        [key: string]: {
            options: {
                column_value: string;
                display_name: string;
                is_applied: boolean | null;
            }[];
            display_name: string;
        };
    };
}

const SurveyPermissions: React.FC<SurveyDetailsProps> = ({ surveyId, handleEditCancel, onSubmitData, preSelectedQuestions, savedDemographicPermissions, savedThreshold, savedDemographicOptions }) => {
    const dispatch = useAppDispatch();
    const surveyDetails = useAppSelector((state) => state.surveyDetails);
    const savedFilters = useAppSelector((state) => state.selectedFilters.selectedFilters);
    const savedDemographics = useAppSelector((state) => state.selectedFilters.selectedDemographics);

    const [thresholdValue, setThresholdValue] = useState<number | "">(savedThreshold || "");
    const [questions, setQuestions] = useState<QuestionListResponse[]>([]);
    const [selectedQuestions, setSelectedQuestions] = useState<QuestionListResponse[]>(preSelectedQuestions.map((q) => ({ id: q.id, name: q.name })));
    const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] }>(savedFilters);
    const [detailedFilters, setDetailedFilters] = useState<Record<string, { options: { column_value: string; display_name: string; is_applied: boolean | null }[]; display_name: string }>>({});
    const [selectedDemographicOptionsFilters, setSelectedDemographicOptionsFilters] = useState<{ [key: string]: string[] }>(savedDemographics);
    const [detailedDemographicOptionsFilters, setDetailedDemographicOptionsFilters] = useState<
        Record<string, { options: { column_value: string; display_name: string; is_applied: boolean | null }[]; display_name: string }>
    >({});

    useEffect(() => {
        return () => {
            dispatch(resetSelectedFilters());
            dispatch(resetDemographicOptions());
        };
    }, []);

    useEffect(() => {
        if (surveyId) {
            dispatch(fetchSurveyDetails(surveyId));
        }
    }, [surveyId]);

    const mapFilters = useCallback((filters: DefaultFilters) => {
        const selected: { [key: string]: string[] } = {};
        const details: Record<string, { options: { column_value: string; display_name: string; is_applied: boolean | null }[]; display_name: string }> = {};

        Object.entries(filters).forEach(([key, category]) => {
            const appliedOptions = category.options.filter((option) => option.is_applied);
            if (appliedOptions.length) {
                selected[key] = appliedOptions.map((option) => option.column_value);
                details[key] = { display_name: category.display_name, options: category.options };
            }
        });

        return { selected, details };
    }, []);

    useEffect(() => {
        if (savedDemographicPermissions) {
            const { selected, details } = mapFilters(savedDemographicPermissions);
            setSelectedFilters(selected);
            setDetailedFilters(details);
            dispatch(setFilters({ selected, details }));
        }
    }, [savedDemographicPermissions, mapFilters]);

    useEffect(() => {
        if (Object.keys(savedDemographicOptions).length > 0) {
            const { selected, details } = mapFilters(savedDemographicOptions);

            setSelectedDemographicOptionsFilters(selected);
            setDetailedDemographicOptionsFilters(details);
            dispatch(setDemographicOptions({ selectedDemographics: selected, detailedDemographics: details }));
        }
    }, [savedDemographicOptions, mapFilters]);

    useEffect(() => {
        const fetchQuestions = async () => {
            if (!surveyId) return;
            try {
                const response = await extendedApiWrapper.getQuestionsList(surveyId);
                if (response.data && Array.isArray(response.data)) {
                    setQuestions(response.data);
                }
            } catch (error) {
                console.error("Error fetching questions:", error);
            }
        };

        fetchQuestions();
    }, [surveyId]);

    const handleQuestionsSelection = (selected: { id: string; name: string }[]) => {
        setSelectedQuestions(selected);
    };

    const handlePresetFilterChange = (detailedFilters: Record<string, any>, formattedFilters: { [key: string]: string[] }, type: "data" | "options") => {
        if (type === "data") {
            setDetailedFilters(detailedFilters);
            setSelectedFilters(formattedFilters);
            dispatch(setFilters({ selected: formattedFilters, details: detailedFilters }));
        } else {
            setDetailedDemographicOptionsFilters(detailedFilters);
            setSelectedDemographicOptionsFilters(formattedFilters);
            dispatch(setDemographicOptions({ selectedDemographics: formattedFilters, detailedDemographics: detailedFilters }));
        }
    };

    const handleFiltersChange = (
        selected: { [key: string]: string[] },
        details: Record<string, { options: { column_value: string; display_name: string; is_applied: boolean | null }[]; display_name: string }>,
        type: "data" | "options",
    ) => {
        if (type === "data") {
            setSelectedFilters(selected);
            setDetailedFilters(details);
            dispatch(setFilters({ selected: selected, details: details }));
        } else {
            setSelectedDemographicOptionsFilters(selected);
            setDetailedDemographicOptionsFilters(details);
            dispatch(setDemographicOptions({ selectedDemographics: selected, detailedDemographics: details }));
        }
    };
    const handleThresholdChange = (value: string) => {
        const numericValue = parseInt(value, 10);

        if (!isNaN(numericValue) && numericValue > 0) {
            setThresholdValue(numericValue);
        } else if (value === "") {
            setThresholdValue(0);
        }
    };

    const handleSubmit = () => {
        const questionIds = selectedQuestions.map((question) => question.id);
        const submissionData = {
            selectedFilters,
            selectedQuestions: questionIds,
            threshold: thresholdValue,
            selectedDemographicOptionsFilters,
        };
        onSubmitData(submissionData);
    };

    const updateOptions = (
        setState: React.Dispatch<React.SetStateAction<Record<string, { options: { column_value: string; display_name: string; is_applied: boolean | null }[]; display_name: string }>>>,
        key: string,
        optionKey: string,
        checked: boolean,
    ) => {
        setState((prev) => ({
            ...prev,
            [key]: {
                ...prev[key],
                options: prev[key]?.options?.map((option) => (option.column_value === optionKey ? { ...option, is_applied: checked } : option)) || [],
            },
        }));
    };

    const onCheckboxClick = (key: string, optionKey: string, checked: boolean) => {
        updateOptions(setDetailedFilters, key, optionKey, checked);
    };

    const onOptionsCheckboxClick = (key: string, optionKey: string, checked: boolean) => {
        updateOptions(setDetailedDemographicOptionsFilters, key, optionKey, checked);
    };

    const isSubmitDisabled =
        !(thresholdValue && thresholdValue > 0) && Object.keys(selectedFilters).length === 0 && Object.keys(selectedDemographicOptionsFilters).length === 0 && selectedQuestions.length === 0;

    return (
        <>
            <div className={styles.permissionsContainer}>
                <div className={styles.demographicContainer}>
                    <div className={styles.demographicText}>Demographic Data Access</div>
                    <DemographicPermissions
                        userPresets={savedDemographicPermissions}
                        onDefaultFiltersChange={(detailedFilters, formattedFilters) => handlePresetFilterChange(detailedFilters, formattedFilters, "data")}
                        onCheckboxClick={onCheckboxClick}
                        editMode
                        onFiltersChange={(selected, details) => handleFiltersChange(selected, details, "data")}
                        filters={surveyDetails.data?.demographic_filters ?? {}}
                        isPreselectedFilters={Object.keys(savedDemographicPermissions).length > 0}
                    />
                </div>
                <div className={styles.demographicContainer}>
                    <div className={styles.demographicValuesHeader}>
                        <img src={Demographic} alt="" />
                        <span>Demographic Data Access</span>
                    </div>
                    <div
                        className={styles.demographicValues}
                        style={{
                            flexDirection: Object.keys(selectedFilters).length > 0 ? "column" : "row",
                        }}
                    >
                        {Object.entries(selectedFilters).length > 0
                            ? Object.entries(selectedFilters).map(([filterKey, filterValues]) => {
                                  const filterDetails = detailedFilters[filterKey];

                                  if (!filterDetails) return null; // Handle case where filterDetails might not exist

                                  const displayName = filterDetails.display_name;

                                  // Filter only the values where `is_applied` is true
                                  const appliedValues = filterDetails.options.filter((option) => option.is_applied).map((option) => option.column_value);

                                  // Check if there are any applied values
                                  if (appliedValues.length === 0) return null;

                                  const displayValues = appliedValues
                                      .slice(0, 2)
                                      .map((value) => {
                                          const valueDetails = filterDetails.options.find((option) => option.column_value === value);
                                          return valueDetails ? valueDetails.display_name : value;
                                      })
                                      .join(", ");

                                  const remainingCount = appliedValues.length - 2;

                                  return (
                                      <div key={filterKey}>
                                          <div className={styles.filterKey}>{displayName}</div>
                                          <div className={styles.filterValues}>
                                              {displayValues}
                                              {remainingCount > 0 && `, ...(+${remainingCount})`}
                                          </div>
                                      </div>
                                  );
                              })
                            : "-"}
                    </div>
                </div>
                <div className={styles.demographicContainer}>
                    <div className={styles.demographicText}>Demographic Options Access</div>
                    <DemographicPermissions
                        userPresets={savedDemographicOptions}
                        onDefaultFiltersChange={(detailedFilters, formattedFilters) => handlePresetFilterChange(detailedFilters, formattedFilters, "options")}
                        onCheckboxClick={onOptionsCheckboxClick}
                        editMode
                        onFiltersChange={(selected, details) => handleFiltersChange(selected, details, "options")}
                        filters={surveyDetails.data?.demographic_filters ?? {}}
                        isPreselectedFilters={Object.keys(savedDemographicOptions).length > 0}
                    />
                </div>
                <div className={styles.demographicContainer}>
                    <div className={styles.demographicValuesHeader}>
                        <img src={Demographic} alt="" />
                        <span>Demographic Options Access</span>
                    </div>
                    <div
                        className={styles.demographicValues}
                        style={{
                            flexDirection: Object.keys(selectedDemographicOptionsFilters).length > 0 ? "column" : "row",
                        }}
                    >
                        {Object.entries(selectedDemographicOptionsFilters).length > 0
                            ? Object.entries(selectedDemographicOptionsFilters).map(([filterKey, filterValues]) => {
                                  const filterDetails = detailedDemographicOptionsFilters[filterKey];

                                  if (!filterDetails) return null; // Handle case where filterDetails might not exist

                                  const displayName = filterDetails.display_name;

                                  // Filter only the values where `is_applied` is true
                                  const appliedValues = filterDetails.options.filter((option) => option.is_applied).map((option) => option.column_value);

                                  // Check if there are any applied values
                                  if (appliedValues.length === 0) return null;

                                  const displayValues = appliedValues
                                      .slice(0, 2)
                                      .map((value) => {
                                          const valueDetails = filterDetails.options.find((option) => option.column_value === value);
                                          return valueDetails ? valueDetails.display_name : value;
                                      })
                                      .join(", ");

                                  const remainingCount = appliedValues.length - 2;

                                  return (
                                      <div key={filterKey}>
                                          <div className={styles.filterKey}>{displayName}</div>
                                          <div className={styles.filterValues}>
                                              {displayValues}
                                              {remainingCount > 0 && `, ...(+${remainingCount})`}
                                          </div>
                                      </div>
                                  );
                              })
                            : "-"}
                    </div>
                </div>

                <div className={styles.demographicContainer}>
                    <div className={styles.demographicText}>Demographic Threshold</div>
                    <TextInput placeholder="Enter Demographic Threshold" value={thresholdValue} onChange={handleThresholdChange} width="32.75rem" />
                </div>
                <div className={styles.demographicContainer}>
                    <div className={styles.demographicValuesHeader}>
                        <img src={Threshold} alt="" />
                        <span>Demographic Threshold</span>
                    </div>
                    <div className={styles.demographicValues}>{thresholdValue ? thresholdValue : "-"}</div>
                </div>
                <div className={styles.demographicContainer}>
                    <div className={styles.demographicText}>Questions</div>
                    <DropdownWithCheckboxes options={questions} label="Select Questions" onSelectionChange={handleQuestionsSelection} preSelected={preSelectedQuestions} />
                </div>
                <div className={styles.demographicContainer}>
                    <div className={styles.demographicValuesHeader}>
                        <img src={Question} alt="" />
                        <span>Questions</span>
                    </div>
                    <div className={styles.demographicValues} style={{ flexDirection: selectedQuestions.length > 0 ? "column" : "row" }}>
                        {" "}
                        {selectedQuestions.length > 0
                            ? selectedQuestions.map((item) => (
                                  <p style={{ display: "flex" }} key={item.id}>
                                      {item.name}
                                      <br />
                                  </p>
                              ))
                            : "-"}
                    </div>
                </div>
            </div>
            <div className={styles.buttons}>
                <CustomCancelButton variant="contained" onClick={handleEditCancel}>
                    Cancel
                </CustomCancelButton>
                <CustomTranslateButton variant="contained" onClick={handleSubmit} disabled={isSubmitDisabled}>
                    Submit
                </CustomTranslateButton>
            </div>
        </>
    );
};
export default SurveyPermissions;
